import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Sidebar } from '../../shared/components/sidebar/sidebar';
import { Navbar } from '../../shared/components/navbar/navbar';
import { PendingReviewsBanner } from '../../shared/components/pending-reviews-banner/pending-reviews-banner';
import { Button } from '../../shared/components/button/button';
import { Toast } from '../../shared/services/toast';
import { Toast as ToastComponent } from '../../shared/components/toast/toast';
import { InferenceService, InferenceResult } from '../../core/services/inference.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

interface KgDocument {
  name: string;
  documentId: string | null;
  updatedAt: string | null;
}

@Component({
  selector: 'app-inferences',
  standalone: true,
  imports: [CommonModule, FormsModule, Sidebar, Navbar, Button, ToastComponent, PendingReviewsBanner],
  templateUrl: './inferences.html',
  styleUrl: './inferences.css'
})
export class InferencesPage implements OnInit {
  private inferenceService = inject(InferenceService);
  private http = inject(HttpClient);
  private toast = inject(Toast);

  // document picker
  showPicker = signal(false);
  documents = signal<KgDocument[]>([]);
  selectedDocNames = signal<Set<string>>(new Set());
  loadingDocs = signal(false);

  // inference results
  results = signal<InferenceResult[]>([]);
  isGenerating = signal(false);
  hasGenerated = signal(false);

  // filters
  severityFilter = signal<string>('all');
  typeFilter = signal<string>('all');
  confidenceFilter = signal<string>('all');
  searchQuery = signal('');

  // detail panel
  selected = signal<InferenceResult | null>(null);

  // status (UI only)
  statuses = signal<Record<string, 'accepted' | 'rejected' | null>>({});

  filtered = computed(() => {
    let items = this.results();
    const sv = this.severityFilter();
    const tf = this.typeFilter();
    const cf = this.confidenceFilter();
    const q  = this.searchQuery().toLowerCase();

    if (sv !== 'all') items = items.filter(i => i.severity === sv);
    if (tf !== 'all') items = items.filter(i => i.inference_type?.toLowerCase() === tf.toLowerCase());
    if (cf !== 'all') {
      const [lo, hi] = cf === 'high' ? [0.85, 1] : cf === 'med' ? [0.7, 0.85] : [0, 0.7];
      items = items.filter(i => i.confidence >= lo && i.confidence < hi);
    }
    if (q) items = items.filter(i =>
      i.topic.toLowerCase().includes(q) || i.inference.toLowerCase().includes(q));
    return items;
  });

  counts = computed(() => ({
    critical: this.results().filter(i => i.severity === 'critical').length,
    high:     this.results().filter(i => i.severity === 'high').length,
    medium:   this.results().filter(i => i.severity === 'medium').length,
    low:      this.results().filter(i => i.severity === 'low').length,
  }));

  inferenceTypes = computed(() =>
    [...new Set(this.results().map(r => r.inference_type).filter(Boolean))]);

  // Email docs always end with " [8hexchars]" (from email_ingestion_service)
  private readonly emailPattern = /\[[0-9a-f]{8}\]$/;
  sharepointDocs = computed(() => this.documents().filter(d => !this.emailPattern.test(d.name)));
  emailDocs      = computed(() => this.documents().filter(d =>  this.emailPattern.test(d.name)));

  ngOnInit() {
    this.loadDocuments();
    this.loadExisting();
  }

  loadExisting() {
    this.inferenceService.getAll().subscribe({
      next: res => {
        const data = res?.data ?? [];
        if (data.length > 0) {
          this.results.set(data);
          this.hasGenerated.set(true);
        }
      },
      error: () => {}
    });
  }

  loadDocuments() {
    this.loadingDocs.set(true);
    this.http.get<{ documents: KgDocument[] }>(`${environment.apiUrl}/knowledge-graph/documents`).subscribe({
      next: res => { this.documents.set(res.documents ?? []); this.loadingDocs.set(false); },
      error: () => { this.loadingDocs.set(false); }
    });
  }

  toggleDoc(name: string) {
    const s = new Set(this.selectedDocNames());
    s.has(name) ? s.delete(name) : s.add(name);
    this.selectedDocNames.set(s);
  }

  selectAll() { this.selectedDocNames.set(new Set(this.documents().map(d => d.name))); }
  clearAll()  { this.selectedDocNames.set(new Set()); }

  openPicker() { this.showPicker.set(true); }
  closePicker() { this.showPicker.set(false); }

  generate() {
    this.closePicker();
    const docNames = this.selectedDocNames().size > 0
      ? Array.from(this.selectedDocNames())
      : null;

    this.isGenerating.set(true);
    this.selected.set(null);

    this.inferenceService.generate(docNames).subscribe({
      next: res => {
        this.results.set(res?.result ?? []);
        this.hasGenerated.set(true);
        this.isGenerating.set(false);
        if (!res?.hasResult) {
          this.toast.show('No inferences found for the selected documents.', 'error');
        }
      },
      error: err => {
        this.isGenerating.set(false);
        this.toast.show(err?.error?.message || 'Failed to generate inferences.', 'error');
      }
    });
  }

  selectInference(inf: InferenceResult) {
    this.selected.set(this.selected()?.topic === inf.topic ? null : inf);
  }

  setStatus(topic: string, status: 'accepted' | 'rejected') {
    const map = { ...this.statuses() };
    map[topic] = map[topic] === status ? null : status;
    this.statuses.set(map);
  }

  documentSourcesList(inf: InferenceResult): string[] {
    const sources = (inf.supporting_facts ?? [])
      .map(f => f.document_source)
      .filter((v): v is string => !!v);
    return [...new Set(sources)];
  }

  factChips(inf: InferenceResult): string[] {
    const entities = (inf.supporting_facts ?? []).flatMap(f => [f.source, f.target]).filter(Boolean);
    return [...new Set(entities)];
  }

  confidencePct(c: number) { return Math.round(c * 100); }

  severityLabel(s: string) {
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  private readonly severityIcons: Record<string, string> = {
    critical: '/assets/icons/severity/critical.svg',
    high: '/assets/icons/severity/high.svg',
    medium: '/assets/icons/severity/medium.svg',
    low: '/assets/icons/severity/low.svg',
  };

  severityIcon(severity: string): string {
    return this.severityIcons[severity] ?? this.severityIcons['low'];
  }
}
